import type { ReactNode } from "react";
import { useGetDisplayInfo } from "../units/use-get-display-info";
import type { PickerRowText } from "./picker-row-text";
import type { PickerStream } from "./picker-stream";

export const ROW_CLASS = "flex min-h-8 w-full items-center gap-2 py-1 pr-2 pl-3.5 text-left";

export function StatusLine({ children }: { children: string }) {
    return <p className={"px-3.5 pt-8 pb-2 text-center text-muted-foreground text-sm"}>{children}</p>;
}

export function GroupSection({
    label,
    count,
    belowBorder = false,
    children,
}: {
    label: string;
    count: number;
    belowBorder?: boolean;
    children: ReactNode;
}) {
    // Under a border, the header's top border sits 1px above the scroll edge, which clips it.
    const edge = belowBorder ? "first:-mt-px" : "first:mt-0";
    const stick = belowBorder ? "-top-px" : "top-0";
    return (
        <div role={"group"} aria-label={label} className={`mt-1.5 flex flex-col ${edge}`}>
            <div
                className={`sticky ${stick} z-[1] flex min-h-[26px] items-center gap-1.5 border-y bg-muted px-3.5 py-[3px]`}
            >
                <span className={"truncate font-semibold text-xs"}>{label}</span>
                <span className={"ml-auto text-muted-foreground text-xs tabular-nums"}>{count}</span>
            </div>
            <div className={"flex flex-col pt-1"}>{children}</div>
        </div>
    );
}

export function RowText({ stream, text: { measurement, detail } }: { stream: PickerStream; text: PickerRowText }) {
    const getDisplayInfo = useGetDisplayInfo();
    const unitSymbol = getDisplayInfo(stream.unitCategory).symbol;
    return (
        <span className={"flex min-w-0 flex-1 items-baseline gap-3"}>
            <span className={"flex min-w-0 items-baseline gap-1.5"}>
                <span className={"truncate text-[13px] leading-4"}>{measurement}</span>
                {unitSymbol && (
                    <span className={"shrink-0 text-[10px] text-muted-foreground leading-4"}>{unitSymbol}</span>
                )}
            </span>
            {detail && (
                <span className={"min-w-16 flex-1 truncate text-right text-muted-foreground/72 text-xs leading-4"}>
                    {detail}
                </span>
            )}
        </span>
    );
}
